// src/utils/firebase/groupCodes.js
// Global reservation map for shareable card-group codes.
// The document ID is the code; uniqueness is this write, not the SKU format.

import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase.utils';
import { getSaleCards } from './releaseCards';
import { sanitizeForFirestore } from './serialization';
import { normalizeSaleStatus } from '../sales/sales.constants';
import { sanitizePreviewCard } from '../groups/groupCodePreview';

export const GROUP_CODES_COLLECTION = 'groupCodes';

/**
 * Canonical form used as the Firestore document ID and for lookup.
 * @param {string} code
 * @returns {string}
 */
export function normalizeGroupCode(code) {
  return String(code ?? '').trim().toUpperCase();
}

const groupCodeRef = (code) => doc(db, GROUP_CODES_COLLECTION, normalizeGroupCode(code));

/**
 * Reserves `code` for `uid`. Returns the normalized code, or null if taken.
 *
 * @param {string} uid
 * @param {string} code
 * @param {{ source?: string, saleId?: string|null }} [meta]
 * @returns {Promise<string|null>}
 */
export async function reserveGroupCode(uid, code, meta = {}) {
  const normalized = normalizeGroupCode(code);
  if (!uid || !normalized) return null;

  const ref = groupCodeRef(normalized);
  const payload = {
    uid,
    source: meta.source ?? 'ebay',
    createdAt: serverTimestamp()
  };
  if (meta.saleId) payload.saleId = meta.saleId;

  try {
    const existing = await getDoc(ref);
    if (existing.exists()) return null;

    await setDoc(ref, payload);
    return normalized;
  } catch (error) {
    // Lost a create race, or the rules rejected a colliding write.
    if (error?.code === 'permission-denied' || error?.code === 'already-exists') {
      return null;
    }
    throw error;
  }
}

const previewUnchanged = (current, next) => (
  (current?.saleId ?? null) === (next.saleId ?? null) &&
  (current?.title ?? '') === (next.title ?? '') &&
  (current?.status ?? '') === (next.status ?? '') &&
  Number(current?.cardCount || 0) === Number(next.cardCount || 0) &&
  JSON.stringify(current?.cards ?? []) === JSON.stringify(next.cards ?? [])
);

const writeGroupCodePreview = async (uid, code, saleId, sale, cards) => {
  const normalized = normalizeGroupCode(code);
  if (!uid || !normalized) return;

  const ref = groupCodeRef(normalized);
  const snap = await getDoc(ref);
  const existing = snap.exists() ? snap.data() : null;
  if (existing && existing.uid !== uid) return;

  if (!existing) {
    try {
      await setDoc(ref, {
        uid,
        source: 'ebay',
        createdAt: serverTimestamp(),
        ...(saleId ? { saleId } : {})
      });
    } catch (error) {
      if (error?.code === 'permission-denied' || error?.code === 'already-exists') return;
      throw error;
    }
  }

  const preview = sanitizeForFirestore({
    saleId: saleId ?? null,
    title: (sale?.title && String(sale.title).trim()) || 'Untitled listing',
    status: normalizeSaleStatus(sale?.status),
    cardCount: (cards ?? []).length,
    cards: (cards ?? []).map(sanitizePreviewCard)
  });

  if (existing && previewUnchanged(existing, preview)) return;

  await updateDoc(ref, preview);
};

/**
 * Copies a sale's public card identity onto its group code for guest preview.
 *
 * @param {string} uid
 * @param {string} saleId
 * @param {{ sale?: Object, cards?: Array }} [preloaded] - Skip re-reading docs the caller already has
 * @returns {Promise<void>}
 */
export async function syncGroupCodePreviewForSale(uid, saleId, preloaded = {}) {
  if (!uid || !saleId) return;

  try {
    let sale = preloaded.sale;
    if (!sale) {
      const saleSnap = await getDoc(doc(db, `users/${uid}/transactions/${saleId}`));
      if (!saleSnap.exists()) return;
      sale = saleSnap.data() ?? {};
    }

    const code = sale.ebaySku;
    if (!code) return;

    const cards = preloaded.cards ?? await getSaleCards(uid, saleId);
    await writeGroupCodePreview(uid, code, saleId, sale, cards);
  } catch {
    // Preview is public-facing and must not fail a sale save. The usual cause
    // is groupCodes rules that have not been deployed yet; the sale document
    // is already the source of truth.
  }
}

/**
 * Points an already-reserved (or legacy unindexed) code at a sale and
 * writes the public preview. Never reassigns a code owned by someone else.
 *
 * @param {string} uid
 * @param {string} code
 * @param {string} saleId
 * @returns {Promise<void>}
 */
export async function attachSaleIdToGroupCode(uid, code, saleId) {
  if (!uid || !saleId) return;
  await syncGroupCodePreviewForSale(uid, saleId);
}

/**
 * Looks up a reserved code. Callers must not assume the sale is readable.
 *
 * @param {string} code
 * @returns {Promise<Object|null>}
 */
export async function getGroupCode(code) {
  const normalized = normalizeGroupCode(code);
  if (!normalized) return null;

  const snap = await getDoc(groupCodeRef(normalized));
  if (!snap.exists()) return null;

  const data = snap.data() ?? {};
  return {
    code: normalized,
    uid: data.uid ?? null,
    source: data.source ?? null,
    saleId: data.saleId ?? null,
    title: data.title ?? null,
    status: data.status ?? null,
    cardCount: data.cardCount ?? (Array.isArray(data.cards) ? data.cards.length : 0),
    cards: Array.isArray(data.cards) ? data.cards : []
  };
}
